(() => {
  let opened = null;
  class StudioSelect extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.select = this.querySelector('select');
      if (!this.select) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.classList.add('studio-select');
      const id = this.select.id;
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.id = `${id}-toggle`;
      this.button.className = 'studio-select-toggle';
      this.button.setAttribute('role', 'combobox');
      this.button.setAttribute('aria-haspopup', 'listbox');
      this.button.setAttribute('aria-expanded', 'false');
      this.button.innerHTML = '<span class="studio-select-value"></span><svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4"/></svg>';
      this.valueLabel = this.button.firstElementChild;
      this.labels = [...this.select.labels];
      this.labels.forEach(label => { label.htmlFor = this.button.id; });
      this.menu = document.createElement('div');
      this.menu.className = 'studio-select-menu';
      this.menu.id = `${id}-listbox`;
      this.menu.setAttribute('role', 'listbox');
      this.menu.hidden = true;
      const label = this.labels.map(item => item.textContent.trim()).join(' ') || this.select.getAttribute('aria-label');
      if (label) this.menu.setAttribute('aria-label', label);
      this.button.setAttribute('aria-controls', this.menu.id);
      this.append(this.button);
      document.body.append(this.menu);
      this.select.hidden = true;
      this.sync();
      this.button.addEventListener('click', () => this.isOpen ? this.close() : this.open(), {signal});
      this.button.addEventListener('keydown', event => this.keydown(event), {signal});
      this.menu.addEventListener('pointerdown', event => event.preventDefault(), {signal});
      this.menu.addEventListener('click', event => {
        const option = event.target.closest('[role="option"]');
        if (option) this.choose(Number(option.dataset.index));
      }, {signal});
      this.menu.addEventListener('pointermove', event => {
        const option = event.target.closest('[role="option"]');
        if (option && option.getAttribute('aria-disabled') !== 'true') this.highlight(Number(option.dataset.index), false);
      }, {signal});
      document.addEventListener('pointerdown', event => {
        if (this.isOpen && !this.contains(event.target) && !this.menu.contains(event.target)) this.close();
      }, {signal});
      document.addEventListener('focusin', event => {
        if (this.isOpen && !this.contains(event.target)) this.close();
      }, {signal});
      window.addEventListener('resize', () => this.isOpen && this.position(), {signal});
      document.addEventListener('scroll', event => {
        if (this.isOpen && event.target !== this.menu) this.position();
      }, {signal, capture: true, passive: true});
      (this.select.form || this).addEventListener('change', () => this.sync(), {signal});
      this.select.form?.addEventListener('reset', () => queueMicrotask(() => this.sync()), {signal});
      // Product options can update their text and availability after a preceding choice.
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.select, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'selected', 'label']});
    }

    disconnectedCallback() {
      this.close();
      this.observer?.disconnect();
      this.controller?.abort();
      this.menu?.remove();
      this.button?.remove();
      this.labels?.forEach(label => { label.htmlFor = this.select.id; });
      if (this.select) this.select.hidden = false;
      this.controller = null;
    }

    sync() {
      this.valueLabel.textContent = this.select.selectedOptions[0]?.label || '';
      this.button.disabled = this.select.disabled;
      const fragment = document.createDocumentFragment();
      [...this.select.options].forEach((option, index) => {
        const item = document.createElement('div');
        item.id = `${this.menu.id}-${index}`;
        item.dataset.index = index;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(option.selected));
        item.setAttribute('aria-disabled', String(option.disabled));
        item.textContent = option.label;
        item.hidden = option.hidden;
        fragment.append(item);
      });
      this.menu.replaceChildren(fragment);
      this.highlight(this.select.selectedIndex, false);
      if (this.isOpen) this.position();
    }

    open() {
      if (this.select.disabled) return;
      opened?.close();
      opened = this;
      this.isOpen = true;
      this.menu.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      document.body.classList.add('select-menu-open');
      this.position();
      this.highlight(this.select.selectedIndex);
    }

    close() {
      this.isOpen = false;
      this.menu && (this.menu.hidden = true);
      this.button?.setAttribute('aria-expanded', 'false');
      this.button?.removeAttribute('aria-activedescendant');
      if (opened === this) { opened = null; document.body.classList.remove('select-menu-open'); }
    }

    position() {
      const box = this.button.getBoundingClientRect();
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const height = viewport?.height || window.innerHeight;
      const width = viewport?.width || window.innerWidth;
      if (box.bottom < top || box.top > top + height) { this.close(); return; }
      const below = top + height - box.bottom - 14;
      const above = box.top - top - 14;
      const flip = below < Math.min(this.menu.scrollHeight, 260) && above > below;
      const menuWidth = Math.min(Math.max(box.width, 250), width - 24);
      this.menu.style.width = `${menuWidth}px`;
      this.menu.style.maxHeight = `${Math.max(80, Math.min(320, flip ? above : below))}px`;
      this.menu.style.left = `${Math.max(12, Math.min(box.left, width - menuWidth - 12))}px`;
      this.menu.style.top = `${flip ? Math.max(top + 12, box.top - this.menu.offsetHeight - 7) : box.bottom + 7}px`;
    }

    highlight(index, scroll = true) {
      this.activeIndex = index;
      [...this.menu.children].forEach(item => item.classList.toggle('is-active', Number(item.dataset.index) === index));
      const active = this.menu.children[index];
      if (this.isOpen && active) {
        this.button.setAttribute('aria-activedescendant', active.id);
        if (scroll) {
          const top = active.offsetTop;
          if (top < this.menu.scrollTop) this.menu.scrollTop = top;
          else if (top + active.offsetHeight > this.menu.scrollTop + this.menu.clientHeight) this.menu.scrollTop = top + active.offsetHeight - this.menu.clientHeight;
        }
      }
    }

    choose(index) {
      const option = this.select.options[index];
      if (!option || option.disabled || option.hidden) return;
      const changed = this.select.selectedIndex !== index;
      this.select.selectedIndex = index;
      this.sync();
      this.close();
      this.button.focus({preventScroll: true});
      if (changed) this.select.dispatchEvent(new Event('change', {bubbles: true}));
    }

    keydown(event) {
      const options = [...this.select.options].map((option,index) => ({option,index})).filter(({option}) => !option.disabled && !option.hidden);
      if (!options.length) return;
      if (event.key === 'Escape' || (event.altKey && event.key === 'ArrowUp')) { if (this.isOpen) { event.preventDefault(); event.stopPropagation(); this.close(); } return; }
      if (event.key === 'Tab') { this.close(); return; }
      if (['Enter', ' '].includes(event.key)) { event.preventDefault(); this.isOpen ? this.choose(this.activeIndex) : this.open(); return; }
      if (['ArrowDown','ArrowUp','Home','End'].includes(event.key)) {
        event.preventDefault();
        const wasOpen = this.isOpen;
        if (!wasOpen) this.open();
        let index = options.findIndex(item => item.index === this.activeIndex);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = options.length - 1;
        else if (wasOpen) index = Math.max(0, Math.min(options.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
        this.highlight(options[Math.max(0,index)].index);
        return;
      }
      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        this.search = Date.now() - (this.searchTime || 0) < 700 ? (this.search || '') + event.key : event.key;
        this.searchTime = Date.now();
        const match = options.find(({option}) => option.label.toLowerCase().startsWith(this.search.toLowerCase()));
        if (match) { if (!this.isOpen) this.open(); this.highlight(match.index); }
      }
    }
  }
  if (!customElements.get('studio-select')) customElements.define('studio-select', StudioSelect);
})();
